import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { usePortalMutation, usePortalQuery } from 'src/Libs/api'
import { initiateDeviceDetectorScan, fetchDeviceDetectorScanStatus, fetchFoundDevices, saveFoundDevices } from '../../../api/device-detector.api'
import { ScanStatusModel, FoundDevicesModel } from '../../../models/device-detector.model'
import { UseMutationResult, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

export const foundDevicesQueryKey = (emcSerialnumber: string | undefined) => ['device-detector', 'found-devices', emcSerialnumber] as const

export const scanStatusQueryKey = (emcSerialnumber: string | undefined) => ['device-detector', 'scan-status', emcSerialnumber] as const

interface UseScanStatusOptions {
  emcSerialnumber: string | undefined
  enabled: boolean
  refetchInterval?: number | false
}

interface UseScanStatusResult {
  data: ScanStatusModel | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => Promise<{ data: ScanStatusModel | undefined }>
}

export const useScanStatus = ({ emcSerialnumber, enabled, refetchInterval = false }: UseScanStatusOptions): UseScanStatusResult => {
  const authFetch = useAuthFetchWithResponse()

  const { data, isLoading, isError, refetch } = usePortalQuery<ScanStatusModel>({
    queryKey: scanStatusQueryKey(emcSerialnumber),
    queryFn: () => fetchDeviceDetectorScanStatus(authFetch, emcSerialnumber),
    enabled: enabled && !!emcSerialnumber,
    refetchInterval,
    staleTime: 0,
  })

  return {
    data: data ?? undefined,
    isLoading,
    isError,
    refetch: async () => {
      const result = await refetch()
      return { data: result.data ?? undefined }
    },
  }
}

interface UseFoundDevicesOptions {
  /** Set to false to reuse an already cached result (e.g. loaded elsewhere for the same emcSerialnumber) without refetching on mount. */
  refetchOnMount?: boolean
}

export const useFoundDevices = (
  emcSerialnumber: string | undefined,
  { refetchOnMount = true }: UseFoundDevicesOptions = {}
): { data: FoundDevicesModel | undefined; isLoading: boolean; isError: boolean } => {
  const authFetch = useAuthFetchWithResponse()

  const { data, isLoading, isError } = usePortalQuery<FoundDevicesModel>({
    queryKey: foundDevicesQueryKey(emcSerialnumber),
    queryFn: () => fetchFoundDevices(authFetch, emcSerialnumber),
    enabled: !!emcSerialnumber,
    staleTime: 0,
    refetchOnMount,
  })

  return { data: data ?? undefined, isLoading, isError }
}

interface InitiateScanVariables {
  emcSerialnumber: string
}

interface SaveFoundDevicesVariables {
  emcSerialnumber: string
  serialNumbers: string[]
}

interface UseDeviceDetectorActionsResult {
  initiateScanMutation: UseMutationResult<void | null, unknown, InitiateScanVariables>
  saveFoundDevicesMutation: UseMutationResult<void | null, unknown, SaveFoundDevicesVariables>
}

export const useDeviceDetectorActions = (): UseDeviceDetectorActionsResult => {
  const authFetch = useAuthFetchWithResponse()

  const initiateScanMutation = usePortalMutation({
    mutationFn: ({ emcSerialnumber }: InitiateScanVariables) => initiateDeviceDetectorScan(authFetch, emcSerialnumber),
  })

  const saveFoundDevicesMutation = usePortalMutation({
    mutationFn: ({ emcSerialnumber, serialNumbers }: SaveFoundDevicesVariables) => saveFoundDevices(authFetch, emcSerialnumber, serialNumbers),
  })

  return { initiateScanMutation, saveFoundDevicesMutation }
}

interface UseDeviceDetectorScanResult {
  scanStatus: ScanStatusModel | undefined
  scanResult: FoundDevicesModel | undefined
  isScanning: boolean
  hasCompleted: boolean
  error: unknown
  startScan: () => Promise<void>
  stopScan: () => void
}

const SCAN_POLL_INTERVAL_MS = 2000

/**
 * Unified scan use-case hook:
 * 1) POST initiate scan
 * 2) poll GET scan status until completed/error
 * 3) GET found-devices only after completion
 */
export const useDeviceDetectorScan = (emcSerialnumber: string | undefined): UseDeviceDetectorScanResult => {
  const authFetch = useAuthFetchWithResponse()
  const queryClient = useQueryClient()
  const cancelledRef = useRef(false)
  const completionHandledRef = useRef(false)
  const initiateScanAbortControllerRef = useRef<AbortController | null>(null)

  const [scanStatus, setScanStatus] = useState<ScanStatusModel | undefined>(undefined)
  const [scanResult, setScanResult] = useState<FoundDevicesModel | undefined>(undefined)
  const [isScanning, setIsScanning] = useState(false)
  const [isPolling, setIsPolling] = useState(false)
  const [hasCompleted, setHasCompleted] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const fetchFoundDevicesMutation = usePortalMutation({
    mutationFn: () => fetchFoundDevices(authFetch, emcSerialnumber),
  })

  const {
    data: scanStatusData,
    isError: isScanStatusError,
    refetch: refetchScanStatus,
  } = useScanStatus({
    emcSerialnumber,
    enabled: isPolling,
    refetchInterval: isPolling ? SCAN_POLL_INTERVAL_MS : false,
  })

  const stopScan = useCallback((): void => {
    cancelledRef.current = true
    initiateScanAbortControllerRef.current?.abort()
    initiateScanAbortControllerRef.current = null
    setIsPolling(false)
    setIsScanning(false)
  }, [])

  const startScan = useCallback(async (): Promise<void> => {
    if (!emcSerialnumber) return

    cancelledRef.current = false
    completionHandledRef.current = false
    initiateScanAbortControllerRef.current?.abort()
    initiateScanAbortControllerRef.current = new AbortController()

    setScanStatus(undefined)
    setScanResult(undefined)
    setHasCompleted(false)
    setError(null)
    setIsPolling(false)
    setIsScanning(true)

    // Ensure a new scan session does not consume stale cached status (e.g. previous 100%).
    queryClient.removeQueries({ queryKey: scanStatusQueryKey(emcSerialnumber), exact: true })

    try {
      const result = await initiateDeviceDetectorScan(authFetch, emcSerialnumber, initiateScanAbortControllerRef.current.signal)

      if (result.hasErrors) {
        throw result.error ?? new Error('Failed to initiate scan')
      }

      if (!cancelledRef.current) {
        setIsPolling(true)
        await refetchScanStatus()
      }
    } catch (scanError) {
      if (!cancelledRef.current) {
        setError(scanError)
        setIsScanning(false)
        setIsPolling(false)
      }
    } finally {
      initiateScanAbortControllerRef.current = null
    }
  }, [authFetch, emcSerialnumber, queryClient, refetchScanStatus])

  useEffect(() => {
    if (!isPolling) return

    if (isScanStatusError) {
      setError(new Error('Failed to fetch scan status'))
      setIsScanning(false)
      setIsPolling(false)
      return
    }

    if (!scanStatusData) return

    setScanStatus(scanStatusData)

    // Scan is only complete when backend reports 100%.
    if (scanStatusData.scanStatus === 100) {
      if (completionHandledRef.current) return
      completionHandledRef.current = true
      setIsPolling(false)

      fetchFoundDevicesMutation
        .mutateAsync()
        .then(foundDevices => {
          if (cancelledRef.current) return
          setScanResult(foundDevices ?? { devices: [] })
          setHasCompleted(true)
          setIsScanning(false)
        })
        .catch(fetchError => {
          if (cancelledRef.current) return
          setError(fetchError)
          setIsScanning(false)
        })
      return
    }

    if (scanStatusData.status === 'running' || (typeof scanStatusData.scanStatus === 'number' && scanStatusData.scanStatus < 100)) {
      return
    }

    if (scanStatusData.status === 'error') {
      setHasCompleted(true)
      setIsScanning(false)
      setIsPolling(false)
      return
    }

    setError(new Error('Unexpected scan status'))
    setIsScanning(false)
    setIsPolling(false)
  }, [fetchFoundDevicesMutation, isPolling, isScanStatusError, scanStatusData])

  useEffect(() => {
    return () => {
      cancelledRef.current = true
      completionHandledRef.current = false
      initiateScanAbortControllerRef.current?.abort()
      initiateScanAbortControllerRef.current = null
    }
  }, [])

  return {
    scanStatus,
    scanResult,
    isScanning,
    hasCompleted,
    error,
    startScan,
    stopScan,
  }
}
